import { getActiveEnvironment } from "../services/environmentService";

/**
 * Cuándo la hora que guardó Business Central es UTC y cuándo es la del terminal.
 *
 * Nadie mandaba la hora del documento, así que BC la ponía con el reloj del
 * servidor, que va en UTC — mientras la hora de apertura del turno viaja en hora
 * local del terminal. Desde el commit del 27/08/2026 el terminal manda
 * `Hora_creacion`, y desde la extensión 1.0.4.57 BC la respeta. Los documentos
 * anteriores siguen guardados con la hora en UTC, y hay que corregirlos al
 * leerlos o caen en un turno que no es el suyo: medido en vivo el 27/08/2026,
 * los tres de las 05:04-05:12 aparecieron dentro de un turno que abrió a las
 * 06:51.
 *
 * ## Cómo se decide, y por qué no basta una fecha
 *
 * Lo primero que se intentó fue comparar la marca contra un instante de corte.
 * No funciona, y la prueba lo demostró: una marca **ya local** leída como si
 * fuera UTC retrocede tantas horas como valga el huso, así que un documento
 * posterior al corte por menos de esas horas se corregía sin necesitarlo. El
 * error no era de 46 minutos sino de seis horas.
 *
 * El discriminador bueno está en el propio documento: **`Id_TPV` lleva la hora
 * del terminal**, en cualquiera de sus dos formatos. Si esa hora coincide con la
 * que guardó BC, la marca ya es local; si no coincide, es UTC. Es exacto, no
 * necesita constante y se decide documento por documento.
 *
 * La constante queda sólo como respaldo para los documentos que no traen
 * `Id_TPV` — hoy, todos los contabilizados: 0 de 269 lo llevan. Ahí se decide
 * por fecha, con la ambigüedad de un día que se aceptó en el acuerdo.
 *
 * ## Cómo se fijó la constante
 *
 * Las tareas 7 y 8 de Business Central viajaron en el mismo binario: la que
 * cambió el reloj y la que rompió OData. Entraron a la vez, así que el momento
 * en que Traer Históricos empezó a devolver 404 es el momento en que BC empezó a
 * guardar la hora del terminal. Ese registro no se conservó, así que el corte
 * queda acotado por dos medidas independientes:
 *
 * - **Límite inferior**, de Business Central: la 1.0.4.57 —la primera versión
 *   que corrió llevando el cambio de reloj— se compiló a las **12:01:57 UTC**, y
 *   una publicación no puede preceder a su binario.
 * - **Límite superior**, medido sobre los datos del ambiente: se compararon los
 *   37 documentos que llevan la hora del terminal en su `Id_TPV` contra la que
 *   guardó BC. El último con desfase es de las **11:12:35 UTC**; el primero que
 *   coincide, de las **12:47:47 UTC**.
 *
 * **No es un dato exacto: es una medición acotada a 45 min 50 s.** Da igual cuál
 * de los dos extremos se tome: no hay ni un documento del ambiente dentro de esa
 * ventana, comprobado sobre las cuatro entidades del 27/08.
 *
 * ## Lo que no cubre
 *
 * El corte real es el más tardío de dos despliegues: el de Business Central y el
 * del propio terminal, porque un documento lleva hora local sólo si el terminal
 * la mandó. Un terminal con una versión vieja sigue recibiendo hora UTC aunque
 * BC ya la respete — pero ése sí trae `Id_TPV`, así que lo resuelve la regla
 * exacta y no la constante.
 */

/**
 * Instante del corte por ambiente, en UTC. Un ambiente que no esté acá no tiene
 * respaldo por fecha: sus documentos sin `Id_TPV` se leen tal como vienen, que
 * es el comportamiento anterior a este cambio y nunca empeora nada.
 */
export const CORTE_POR_AMBIENTE = {
  Corporativo_Santillana_1_Test: "2026-08-27T12:47:47Z",
};

/**
 * @param {string} ambiente Nombre del ambiente de Business Central.
 * @returns {number|null} El corte en milisegundos, o null si no se conoce.
 */
export const corteDelAmbiente = (ambiente) => {
  const corte = CORTE_POR_AMBIENTE[ambiente];
  if (!corte) return null;

  const instante = Date.parse(corte);

  return Number.isNaN(instante) ? null : instante;
};

/**
 * La hora del terminal que viaja dentro del `Id_TPV`.
 *
 * Conviven dos formatos: el nuevo, `CAJA-YYYYMMDD-HHmmss-n`, con segundos; y el
 * anterior, `YYYYMMDD-HHmm-n`, sólo hasta el minuto.
 *
 * @returns {string|null} `HH:mm:ss` o `HH:mm`, o null si no se puede leer.
 */
export const horaDelIdTpv = (idTpv) => {
  const conSegundos = /(?:^|-)\d{8}-(\d{2})(\d{2})(\d{2})-/.exec(idTpv || "");
  if (conSegundos) return `${conSegundos[1]}:${conSegundos[2]}:${conSegundos[3]}`;

  const alMinuto = /(?:^|-)\d{8}-(\d{2})(\d{2})-/.exec(idTpv || "");
  if (alMinuto) return `${alMinuto[1]}:${alMinuto[2]}`;

  return null;
};

const SEGUNDOS_DEL_DIA = 24 * 60 * 60;

/**
 * Trae la hora a la del terminal **sin tocar la fecha**.
 *
 * Business Central guarda las dos cosas en relojes distintos: `Posting_Date` es
 * la fecha local del documento y `Hora_creacion` la hora UTC. Para una venta de
 * la tarde en América son de días diferentes — comprobado sobre
 * PVMDSC3-000002281, creado el 26/08 a las 21:42:43 locales (UTC−6), que quedó
 * guardado con fecha `2026-08-26` y hora `03:42:45`, que es del 27 en UTC.
 *
 * Por eso no se convierte la marca entera: restarle el huso le quitaría un día a
 * la fecha, que ya era la correcta. Se corrige sólo la hora, y si al restar
 * cruza la medianoche se da la vuelta dentro del mismo día — que es justo lo que
 * deshace el cruce que había introducido el UTC.
 *
 * El huso es el del terminal que lee. Es exacto mientras sea el mismo que el del
 * que escribió; en otro caso yerra en una hora, que no cambia a qué turno
 * pertenece un documento. Riesgo aceptado en el acuerdo del huso.
 */
export const convertirDeUtcALocal = (fechaHora) => {
  const [fecha, hora] = String(fechaHora).split("T");
  const partes = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(hora || "");
  if (!partes) return fechaHora;

  // getTimezoneOffset da los minutos que hay que SUMAR a la hora local para
  // llegar a UTC, así que para el camino inverso se restan.
  const desfase = new Date().getTimezoneOffset() * 60;
  const enSegundos = +partes[1] * 3600 + +partes[2] * 60 + (+partes[3] || 0);
  const local = (((enSegundos - desfase) % SEGUNDOS_DEL_DIA) + SEGUNDOS_DEL_DIA) % SEGUNDOS_DEL_DIA;

  const dos = (n) => String(n).padStart(2, "0");

  return `${fecha}T${dos(Math.floor(local / 3600))}:${dos(Math.floor((local % 3600) / 60))}:${dos(
    local % 60
  )}`;
};

/**
 * ¿La hora que guardó Business Central está en UTC?
 *
 * @param {string} horaGuardada `HH:mm:ss` o con milisegundos, como la publica BC.
 * @param {string} fecha        `YYYY-MM-DD` del documento.
 * @param {string} idTpv        Identificador del terminal, si lo trae.
 * @param {number|null} corte   Respaldo por fecha, en milisegundos.
 */
export const estaEnUtc = (horaGuardada, fecha, idTpv, corte) => {
  const horaDelTerminal = horaDelIdTpv(idTpv);

  // Regla exacta: el documento lleva las dos horas y se comparan.
  if (horaDelTerminal) {
    const largo = horaDelTerminal.length;
    return String(horaGuardada).slice(0, largo) !== horaDelTerminal;
  }

  // Respaldo: sin identificador no hay con qué comparar. Se decide por fecha, y
  // el día del corte cuenta como anterior — un documento de ese día sin `Id_TPV`
  // viene de un terminal que no mandaba la hora, así que BC la puso en UTC.
  if (!corte) return false;

  const finDelDiaDelCorte = `${new Date(corte).toISOString().slice(0, 10)}T23:59:59Z`;

  return Date.parse(`${fecha}T00:00:00Z`) <= Date.parse(finDelDiaDelCorte);
};

/**
 * La marca del documento en hora local del terminal, corregida si hacía falta.
 *
 * @param {Object} apiObj      Documento tal como lo devuelve Business Central.
 * @param {string} fechaHora   `YYYY-MM-DDTHH:mm:ss`, ya compuesta.
 * @param {number|null} corte  Respaldo por fecha, en milisegundos.
 */
export const aHoraLocalDelTerminal = (apiObj, fechaHora, corte) => {
  if (typeof fechaHora !== "string" || !fechaHora.includes("T")) return fechaHora;

  const [fecha, hora] = fechaHora.split("T");
  if (!estaEnUtc(hora, fecha, apiObj?.Id_TPV, corte)) return fechaHora;

  return convertirDeUtcALocal(fechaHora);
};

/**
 * El corte del ambiente que está activo ahora mismo.
 *
 * Se resuelve una vez por lote y se reparte a los mapeadores: leerlo por
 * documento sería una consulta a la base local por cada fila del histórico.
 *
 * @returns {Promise<number|null>}
 */
export const corteDelAmbienteActivo = async () => {
  try {
    const { env } = await getActiveEnvironment();
    return corteDelAmbiente(env);
  } catch {
    // Sin ambiente resuelto no se corrige: mejor dejar el dato como vino que
    // moverlo cinco horas por una suposición.
    return null;
  }
};
